// Floating AI Assistant window. Rendered via portal into document.body so it
// floats above the app regardless of route. Draggable by the header, resizable
// via the bottom-right handle. Uses the MobX aiStore for all state.

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import { observer } from "mobx-react-lite";
import {
  Sparkles,
  Plus,
  X,
  Send,
  Bot,
  User as UserIcon,
  Clock,
  ChevronRight,
  ChevronLeft,
  Wrench,
  Check,
  Loader2,
  Trash2,
  Search,
  Info,
  Activity,
  Brain,
  ArrowDown,
  Square,
} from "lucide-react";
import { aiStore, type ChatMessageView } from "../../stores/ai.store";
import { ConfirmCard } from "./ConfirmCard";
import { Markdown } from "./Markdown";

// Progressive (typewriter) reveal that tracks the real incoming token rate so
// the writing effect never lags behind streaming. We sample how fast `content`
// grows and reveal at roughly that rate (with a floor so very slow streams
// still animate), then snap to full when streaming ends.
function useTypewriter(content: string, streaming: boolean): string {
  const [shown, setShown] = useState(content.length);
  const ref = useRef(content);
  ref.current = content;
  const lastLen = useRef(content.length);
  const lastT = useRef(performance.now());
  const rate = useRef(8); // chars per 18ms tick, adaptive

  useEffect(() => {
    if (content.length <= shown) return;
    const now = performance.now();
    const dt = Math.max(1, now - lastT.current);
    const added = content.length - lastLen.current;
    if (added > 0) {
      // chars-per-tick estimate from real arrival rate, clamped to sane bounds.
      const perTick = (added / dt) * 18;
      rate.current = Math.max(3, Math.min(120, perTick * 1.15));
    }
    lastLen.current = content.length;
    lastT.current = now;

    const id = setInterval(() => {
      setShown((s) => {
        const target = ref.current.length;
        if (s >= target) {
          clearInterval(id);
          return s;
        }
        return Math.min(target, s + Math.round(rate.current));
      });
    }, 18);
    return () => clearInterval(id);
  }, [content, streaming]);

  useEffect(() => {
    if (!streaming) {
      lastLen.current = ref.current.length;
      lastT.current = performance.now();
      setShown(ref.current.length);
    }
  }, [streaming]);

  return content.slice(0, Math.min(shown, content.length));
}

const MessageBubble = observer(function MessageBubble({ msg }: { msg: ChatMessageView }) {
  const isUser = msg.role === "user";
  const text = useTypewriter(msg.content, Boolean(msg.streaming));
  const isPending = (id: string) => aiStore.pending.some((p) => p.toolCallId === id);
  return (
    <div className={`flex max-w-[88%] flex-col gap-1.5 ${isUser ? "self-end items-end" : "self-start items-start"}`}>
      <div className={`flex max-w-full gap-2 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
        <span
          className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
            isUser ? "bg-ember/20 text-ember" : "bg-surface-2 text-fg-muted"
          }`}
        >
          {isUser ? <UserIcon size={13} /> : <Bot size={13} />}
        </span>
        <div
          className={`whitespace-pre-wrap break-words rounded-2xl border px-3 py-2 text-[13px] leading-relaxed ${
            isUser ? "border-ember/50 bg-ember/15 text-fg" : "border-line bg-surface-2 text-fg"
          }`}
        >
          {isUser ? (
            msg.content || (msg.streaming ? "…" : "")
          ) : (
            <>
              {msg.content ? (
                <Markdown content={text} />
              ) : msg.streaming ? (
                <span className="inline-flex items-center gap-1.5 text-fg-muted">
                  <span className="gm-live-dot h-1.5 w-1.5 rounded-full bg-ember" />
                  Denkt…
                </span>
              ) : null}
              {msg.streaming && text.length < msg.content.length && (
                <span className="ml-0.5 inline-block h-3.5 w-[2px] animate-pulse bg-ember align-middle" />
              )}
            </>
          )}
        </div>
      </div>
      {msg.toolCalls && msg.toolCalls.some((t) => isPending(t.id)) && (
        <div className="flex w-full max-w-full flex-col gap-1.5 pl-8">
          {msg.toolCalls
            .filter((t) => isPending(t.id))
            .map((t) => (
              <ConfirmCard
                key={t.id}
                pending={[t]}
                onApproveAll={() => aiStore.approveAll()}
                onRejectAll={() => aiStore.rejectAll()}
                onSubmit={(decisions) => void aiStore.submitDecisions(decisions)}
              />
            ))}
        </div>
      )}
    </div>
  );
});

function fmtMs(ms: number | null): string {
  if (ms == null) return "–";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

// Small stats panel: response-time metrics measured client-side during the
// last turn, plus the active model. TPS/CPU are per-server and not in scope
// of the assistant window, so they are intentionally omitted.
function AiDetails() {
  const m = aiStore.metrics;
  const u = aiStore.lastUsage;
  const firstTok = m.firstTokenAt && m.startedAt ? m.firstTokenAt - m.startedAt : null;
  const total = m.endedAt && m.startedAt ? m.endedAt - m.startedAt : null;
  const thinkingChars = m.thinkingChars || 0;
  const outputChars = m.outputChars || 0;
  const totalChars = thinkingChars + outputChars;
  const secs = total && total > 0 ? total / 1000 : 0;
  // Token counts are estimates (~4 chars/token) for the client-measured stream.
  const estTokens = (n: number) => (n > 0 ? String(Math.round(n / 4)) : "–");
  // Prefer real provider output tokens for throughput when available.
  const realOutput = u && typeof u.output === "number" ? u.output : null;
  const tokPerSec =
    realOutput != null && secs > 0
      ? (realOutput / secs).toFixed(1)
      : totalChars > 0 && secs > 0
        ? (totalChars / 4 / secs).toFixed(1)
        : "–";
  const model =
    aiStore.config?.diagnosisProfileExplicit && aiStore.config.diagnosisProfile?.model
      ? aiStore.config.diagnosisProfile.model
      : aiStore.config?.chatProfile?.model || "–";

  const Row = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-center justify-between border-b border-line/60 py-1 last:border-0">
      <span className="text-fg-muted">{label}</span>
      <span className="font-mono text-fg">{value}</span>
    </div>
  );

  const fmtTok = (n: number | null | undefined) => (typeof n === "number" ? n.toLocaleString("de-DE") : "–");

  return (
    <div>
      <Row label="Modell" value={model} />
      <Row label="Antwort-Zeit" value={fmtMs(total)} />
      <Row label="1. Token (Latenz)" value={fmtMs(firstTok)} />

      {u ? (
        <>
          <div className="mt-2 mb-1 text-[10px] font-semibold uppercase tracking-wide text-fg-muted">Tokens (Provider)</div>
          <Row label="Eingehend (Input)" value={fmtTok(u.input)} />
          <Row label="Ausgehend (Output)" value={fmtTok(u.output)} />
          {typeof u.cached === "number" && <Row label="Gecacht (Cache)" value={fmtTok(u.cached)} />}
          {typeof u.reasoning === "number" && <Row label="Reasoning" value={fmtTok(u.reasoning)} />}
          <Row label="Gesamt" value={fmtTok(u.total)} />
        </>
      ) : (
        <>
          {thinkingChars > 0 && <Row label="Denk-Tokens (ca.)" value={estTokens(thinkingChars)} />}
          <Row label="Antwort-Tokens (ca.)" value={estTokens(outputChars)} />
        </>
      )}

      <Row label="Durchsatz" value={tokPerSec !== "–" ? `${tokPerSec} tok/s` : "–"} />
      <div className="mt-2 text-[10px] leading-relaxed text-fg-muted">
        {u
          ? "Exakte Token-Zahlen vom Provider (OpenRouter/OpenAI). 'Gecacht' = wiederverwendete, bereits bezahlte Eingabe-Tokens."
          : "Token-Zahlen sind Schätzwerte (≈4 Zeichen/Token) aus generierten Zeichen – inkl. Denkprozess. 'Durchsatz' misst die gesamte Modell-Ausgabe (Denken + Antwort) – kein Server-TPS."}
      </div>
    </div>
  );
}

function toolResultText(data: unknown): string {
  if (typeof data === "string") return data;
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
}

export const AiAssistant = observer(function AiAssistant() {
  const [drag, setDrag] = useState<{ dx: number; dy: number; el: HTMLElement } | null>(null);
  const [resizing, setResizing] = useState<{ dir: string; sx: number; sy: number; sl: number; st: number; sw: number; sh: number; el: HTMLElement } | null>(null);
  const [draft, setDraft] = useState("");
  const [search, setSearch] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [toolIdx, setToolIdx] = useState(0);
  const [autoScroll, setAutoScroll] = useState(true);
  const [atBottom, setAtBottom] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const winRef = useRef<HTMLDivElement>(null);

  const jumpToBottom = () => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setAutoScroll(true);
  };

  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [aiStore.messages.length, aiStore.pending.length, aiStore.streaming, aiStore.error, aiStore.thinking, aiStore.tools.length, autoScroll]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    setAtBottom(near);
    if (near) setAutoScroll(true);
  };

  if (!aiStore.open) return null;

  // --- Dragging: move the element directly via style (no React re-render per move) ---
  const onHeaderDown = (e: ReactPointerEvent) => {
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest("button")) return;
    const el = (e.currentTarget as HTMLElement).parentElement as HTMLElement;
    const rect = el.getBoundingClientRect();
    setDrag({ dx: e.clientX - rect.left, dy: e.clientY - rect.top, el });
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onHeaderMove = (e: ReactPointerEvent) => {
    if (!drag) return;
    const x = Math.max(0, e.clientX - drag.dx);
    const y = Math.max(0, e.clientY - drag.dy);
    drag.el.style.left = `${x}px`;
    drag.el.style.top = `${y}px`;
    drag.el.style.right = "auto";
    drag.el.style.bottom = "auto";
    drag.el.style.willChange = "left, top";
  };
  const onHeaderUp = (e: ReactPointerEvent) => {
    if (!drag) return;
    drag.el.style.willChange = "auto";
    const rect = drag.el.getBoundingClientRect();
    aiStore.setPos(rect.left, rect.top);
    setDrag(null);
    aiStore.persistGeometry();
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  // --- Resizing from any edge or corner ---
  const RESIZE_MIN_W = 320;
  const RESIZE_MIN_H = 360;
  const onResizeDown = (e: ReactPointerEvent, dir: string) => {
    e.stopPropagation();
    const el = (e.currentTarget as HTMLElement).parentElement as HTMLElement;
    const rect = el.getBoundingClientRect();
    setResizing({ dir, sx: e.clientX, sy: e.clientY, sl: rect.left, st: rect.top, sw: rect.width, sh: rect.height, el });
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onResizeMove = (e: ReactPointerEvent) => {
    if (!resizing) return;
    const { dir, sx, sy, sl, st, sw, sh, el } = resizing;
    const dx = e.clientX - sx;
    const dy = e.clientY - sy;
    let left = sl;
    let top = st;
    let width = sw;
    let height = sh;
    if (dir.includes("e")) width = Math.max(RESIZE_MIN_W, sw + dx);
    if (dir.includes("s")) height = Math.max(RESIZE_MIN_H, sh + dy);
    if (dir.includes("w")) {
      const w = Math.max(RESIZE_MIN_W, sw - dx);
      left = sl + (sw - w);
      width = w;
    }
    if (dir.includes("n")) {
      const h = Math.max(RESIZE_MIN_H, sh - dy);
      top = st + (sh - h);
      height = h;
    }
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    el.style.width = `${width}px`;
    el.style.height = `${height}px`;
    el.style.right = "auto";
    el.style.bottom = "auto";
    el.style.willChange = "width, height, left, top";
  };
  const onResizeUp = (e: ReactPointerEvent) => {
    if (!resizing) return;
    resizing.el.style.willChange = "auto";
    const rect = resizing.el.getBoundingClientRect();
    aiStore.setPos(rect.left, rect.top);
    aiStore.setSize(rect.width, rect.height);
    setResizing(null);
    aiStore.persistGeometry();
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const send = () => {
    const text = draft.trim();
    if (!text || aiStore.streaming) return;
    setDraft("");
    void aiStore.sendMessage(text);
  };

  const stopBtn = (e: ReactPointerEvent) => e.stopPropagation();

  const win: CSSProperties = {
    position: "fixed",
    left: aiStore.pos ? aiStore.pos.x : 24,
    top: aiStore.pos ? aiStore.pos.y : 24,
    width: aiStore.size.w,
    height: aiStore.size.h,
    zIndex: 9999,
  };

  return createPortal(
    <div
      ref={winRef}
      style={win}
      role="dialog"
      aria-label="KI-Assistent"
      className={`relative flex flex-col overflow-hidden rounded-2xl border border-line/80 bg-surface text-fg shadow-2xl shadow-black/40 ${drag || resizing ? "select-none" : ""}`}
    >
      <div
        onPointerDown={onHeaderDown}
        onPointerMove={onHeaderMove}
        onPointerUp={onHeaderUp}
        className={`flex select-none items-center justify-between gap-2 border-b border-line bg-gradient-to-r from-surface-2 to-surface px-3 py-2 ${
          drag ? "cursor-grabbing" : "cursor-grab"
        }`}
      >
        <strong className="flex items-center gap-2 text-sm font-semibold text-fg">
          <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-ember/20 text-ember">
            <Sparkles size={14} />
          </span>
          <span className="bg-gradient-to-r from-ember to-amber-300 bg-clip-text text-transparent">
            KI-Assistent
          </span>
        </strong>
        {aiStore.streaming && (
          <span className="flex items-center gap-1.5 rounded-full border border-ember/40 bg-ember/10 px-2 py-0.5 text-[11px] font-medium text-ember">
            <Loader2 size={12} className="animate-spin" />
            {aiStore.metrics.firstTokenAt ? "Antwortet…" : aiStore.thinking.length > 0 ? "Denkt…" : "Startet…"}
          </span>
        )}
        <div className="flex gap-1.5">
          {aiStore.tools.length > 0 && (
            <button
              onPointerDown={stopBtn}
              onClick={() => {
                setToolIdx(0);
                setToolsOpen(true);
              }}
              title="Tool-Aufrufe ansehen"
              className="flex h-7 items-center gap-1.5 rounded-lg border border-line px-2 text-xs text-fg transition hover:border-ember/60 hover:bg-ember/15 hover:text-ember"
            >
              <Wrench size={14} />
              <span>Tool-Calls</span>
              <span className="rounded bg-ember/20 px-1 text-[10px] font-semibold text-ember">
                {aiStore.tools.length}
              </span>
            </button>
          )}
          <button
            onPointerDown={stopBtn}
            onClick={() => aiStore.toggleHistory()}
            title="Verlauf"
            className={`flex h-7 w-7 items-center justify-center rounded-lg border border-line transition hover:border-ember/60 hover:bg-ember/15 hover:text-ember ${
              aiStore.historyOpen ? "border-ember/60 bg-ember/15 text-ember" : "text-fg"
            }`}
          >
            <Clock size={15} />
          </button>
          <button
            onPointerDown={stopBtn}
            onClick={() => aiStore.newChat()}
            title="Neuer Chat"
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-line text-fg transition hover:border-ember/60 hover:bg-ember/15 hover:text-ember"
          >
            <Plus size={15} />
          </button>
          <button
            onPointerDown={stopBtn}
            onClick={() => aiStore.closeWindow()}
            title="Schließen"
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-line text-fg transition hover:border-red-500/60 hover:bg-red-500/15 hover:text-red-300"
          >
            <X size={15} />
          </button>
        </div>
      </div>

      <div ref={scrollRef} onScroll={onScroll} className="relative flex flex-1 flex-col gap-3 overflow-y-auto p-3">

        {!atBottom && (
          <button
            onClick={jumpToBottom}
            title="Zum neuesten Beitrag springen"
            className="sticky bottom-1 z-20 mx-auto mb-1 flex items-center gap-1 self-center rounded-full border border-ember/60 bg-ember/20 px-3 py-1 text-[11px] font-medium text-ember shadow-lg backdrop-blur transition hover:bg-ember/30"
          >
            <ArrowDown size={13} /> Neuste
          </button>
        )}
        {aiStore.messages.length === 0 && !aiStore.streaming && (
          <div className="mx-auto mt-6 max-w-[90%] rounded-xl border border-line/60 bg-surface-2/50 p-3 text-center text-xs leading-relaxed text-fg-muted">
            <Sparkles size={18} className="mx-auto mb-1.5 text-ember" />
            Frag mich z.B.:<br />
            „Warum startet der Survival-Server nicht?“<br />
            „Zeige die letzten Fehler im Log.“
          </div>
        )}

        {aiStore.messages.map((m) => (
          <MessageBubble key={m.id} msg={m} />
        ))}

        {aiStore.lastTurnTruncated && !aiStore.streaming && !aiStore.tools.length && (
          <div className="self-center flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-200">
            <span>Antwort wurde vom Modell abgeschnitten (Token-Limit).</span>
            <button
              onClick={() => void aiStore.sendMessage("weiter")}
              className="rounded-md border border-amber-400/50 bg-amber-400/10 px-2 py-0.5 font-medium hover:bg-amber-400/20"
            >
              weiter
            </button>
          </div>
        )}

        {aiStore.error && (
          <div className="self-center rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-xs text-red-300">
            Fehler: {aiStore.error}
          </div>
        )}
      </div>

      <div className="relative flex items-end gap-1.5 border-t border-line bg-surface-2/40 p-2">
        <button
          onClick={() => setDetailsOpen((v) => !v)}
          title="Details & Statistik"
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition ${
            detailsOpen ? "border-ember/60 bg-ember/15 text-ember" : "border-line text-fg hover:bg-white/5"
          }`}
        >
          <Activity size={16} />
        </button>
        <button
          onPointerDown={stopBtn}
          onClick={() => aiStore.toggleThinking()}
          title="Denkprozess"
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition ${
            aiStore.thinkingOpen ? "border-ember/60 bg-ember/15 text-ember" : "border-line text-fg hover:bg-white/5"
          }`}
        >
          <Brain size={16} />
        </button>
        <button
          onPointerDown={stopBtn}
          onClick={() => {
            if (autoScroll) setAutoScroll(false);
            else jumpToBottom();
          }}
          title={autoScroll ? "Autoscroll: an (Klicken zum Auschalten)" : "Autoscroll: aus (Klicken zum Einschalten & springen)"}
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition ${
            autoScroll ? "border-ember/60 bg-ember/15 text-ember" : "border-line text-fg hover:bg-white/5"
          }`}
        >
          <ArrowDown size={16} />
        </button>
        <textarea
          rows={1}
          value={draft}
          disabled={aiStore.streaming}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="Nachricht schreiben… (Enter zum Senden)"
          className="max-h-32 min-h-[40px] flex-1 resize-none rounded-xl border border-line bg-surface px-3 py-2 text-[13px] text-fg outline-none placeholder:text-fg-muted focus:border-ember/60"
        />
        <button
          onClick={aiStore.streaming ? () => aiStore.stop() : send}
          disabled={!aiStore.streaming && !draft.trim()}
          title={aiStore.streaming ? "Agent stoppen" : "Senden"}
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition ${
            aiStore.streaming
              ? "border-red-500/60 bg-red-500/20 text-red-300 hover:bg-red-500/30"
              : "border-ember bg-ember/20 text-ember hover:bg-ember/30 disabled:opacity-40"
          }`}
        >
          {aiStore.streaming ? (
            <Square size={14} fill="currentColor" />
          ) : (
            <Send size={16} />
          )}
        </button>

        {detailsOpen && (
          <div className="absolute bottom-[60px] left-2 z-30 w-64 rounded-xl border border-line bg-surface p-3 text-[12px] shadow-2xl shadow-black/40">
            <div className="mb-2 flex items-center justify-between">
              <span className="flex items-center gap-1.5 font-semibold text-fg">
                <Info size={14} className="text-ember" /> Details
              </span>
              <button
                onClick={() => setDetailsOpen(false)}
                title="Schließen"
                className="flex h-6 w-6 items-center justify-center rounded-lg border border-line text-fg-muted hover:text-fg"
              >
                <X size={13} />
              </button>
            </div>
            <AiDetails />
          </div>
        )}
      </div>

      {aiStore.thinkingOpen && aiStore.thinking.length > 0 && (
        <div className="absolute bottom-[60px] right-2 z-30 w-72 rounded-xl border border-line bg-surface p-3 text-[12px] shadow-2xl shadow-black/40">
          <div className="mb-2 flex items-center justify-between">
            <span className="flex items-center gap-1.5 font-semibold text-fg">
              <Brain size={14} className="text-ember" /> Denkprozess
            </span>
            <button
              onClick={() => aiStore.toggleThinking()}
              title="Schließen"
              className="flex h-6 w-6 items-center justify-center rounded-lg border border-line text-fg-muted hover:text-fg"
            >
              <X size={13} />
            </button>
          </div>
          <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap break-words rounded-lg border border-ember/20 bg-black/30 p-2 text-[11px] leading-relaxed text-fg-muted">
            {aiStore.thinking}
          </pre>
        </div>
      )}

      {aiStore.historyOpen && (
        <div className="absolute inset-0 z-10 flex flex-col bg-surface">
          <div className="flex items-center justify-between border-b border-line bg-surface-2 px-3 py-2 text-sm font-semibold text-fg">
            <span className="flex items-center gap-1.5">
              <Clock size={15} className="text-ember" /> Verlauf
            </span>
            <button
              onClick={() => aiStore.toggleHistory()}
              title="Schließen"
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-line text-fg transition hover:border-red-500/60 hover:bg-red-500/15 hover:text-red-300"
            >
              <X size={15} />
            </button>
          </div>
          <div className="border-b border-line px-2 py-2">
            <div className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-2">
              <Search size={13} className="shrink-0 text-fg-muted" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Verlauf durchsuchen…"
                className="w-full bg-transparent py-1.5 text-[13px] text-fg outline-none placeholder:text-fg-muted"
              />
              {search && (
                <button onClick={() => setSearch("")} title="Zurücksetzen" className="text-fg-muted hover:text-fg">
                  <X size={13} />
                </button>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {(() => {
              const q = search.trim().toLowerCase();
              const list = q
                ? aiStore.conversations.filter((c) => c.title.toLowerCase().includes(q))
                : aiStore.conversations;
              return (
                <>
                  {list.length === 0 && (
                    <div className="p-3 text-center text-xs text-fg-muted">
                      {q ? "Keine Treffer." : "Keine Gespräche."}
                    </div>
                  )}
                  {list.map((c) => (
                    <div
                      key={c.id}
                      className={`group mb-1.5 flex items-center gap-1.5 rounded-lg border px-3 py-2 transition ${
                        aiStore.activeId === c.id ? "border-ember/60 bg-ember/10" : "border-line hover:bg-surface-2"
                      }`}
                    >
                      <button
                        onClick={() => {
                          void aiStore.loadConversation(c.id);
                          aiStore.toggleHistory();
                        }}
                        className="min-w-0 flex-1 text-left"
                      >
                        <div className="truncate text-[13px] text-fg">{c.title}</div>
                        <div className="text-[10px] text-fg-muted">
                          {c.updated_at ? new Date(c.updated_at).toLocaleString() : ""}
                        </div>
                      </button>
                      <button
                        onClick={() => void aiStore.deleteConversation(c.id)}
                        title="Gespräch löschen"
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-line text-fg-muted opacity-0 transition hover:border-red-500/60 hover:bg-red-500/15 hover:text-red-300 group-hover:opacity-100"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {toolsOpen && aiStore.tools.length > 0 && (() => {
        const idx = Math.min(toolIdx, aiStore.tools.length - 1);
        const t = aiStore.tools[idx];
        if (!t) return null;
        const done = !t.running && t.result;
        return (
          <div
            className="absolute inset-0 z-20 flex items-center justify-center bg-black/60 p-3"
          >
            <div
              className="flex h-[480px] max-h-[82vh] w-[440px] max-w-[92vw] flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-2xl"
            >
              <div className="flex shrink-0 items-center justify-between gap-2 border-b border-line p-3">
                <strong className="flex items-center gap-1.5 text-ember">
                  <Wrench size={14} /> Tool-Aufrufe ({idx + 1}/{aiStore.tools.length})
                </strong>
                <button
                  onClick={() => setToolsOpen(false)}
                  title="Schließen"
                  className="flex h-7 w-7 items-center justify-center rounded-lg border border-line text-fg transition hover:border-red-500/60 hover:bg-red-500/15 hover:text-red-300"
                >
                  <X size={14} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-3" onClick={(e) => e.stopPropagation()}>
                <div className="mb-2 flex items-center gap-1.5 text-[13px]">
                  {t.running ? (
                    <Loader2 size={13} className="shrink-0 animate-spin text-ember" />
                  ) : done ? (
                    <Check size={13} className="shrink-0 text-emerald-400" />
                  ) : (
                    <Wrench size={13} className="shrink-0 text-ember/80" />
                  )}
                  <span className="font-semibold text-fg">{t.name}</span>
                  {t.danger && (
                    <span className="rounded border border-red-500/50 bg-red-500/10 px-1 text-[10px] text-red-300">gefährlich</span>
                  )}
                </div>
                <div className="mb-2 text-[11px] text-fg-muted">{t.summary}</div>

                <div className="mb-1 text-[11px] font-semibold text-fg-muted">Argumente</div>
                <pre className="mb-3 max-h-40 overflow-auto rounded-lg border border-line bg-black/40 p-2 text-[11px] leading-relaxed text-fg">
                  {JSON.stringify(t.arguments, null, 2)}
                </pre>

                <div className="mb-1 text-[11px] font-semibold text-fg-muted">Ergebnis</div>
                {t.result ? (
                  <pre className="max-h-56 overflow-auto rounded-lg border border-line bg-black/40 p-2 text-[11px] leading-relaxed text-fg">
                    {toolResultText(t.result.data)}
                  </pre>
                ) : (
                  <div className="text-[11px] text-fg-muted">Wird ausgeführt…</div>
                )}
              </div>

              {aiStore.tools.length > 1 && (
                <div className="flex shrink-0 items-center justify-between border-t border-line p-3">
                  <button
                    onClick={() => setToolIdx((i) => Math.max(0, i - 1))}
                    disabled={idx === 0}
                    className="flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-xs text-fg transition hover:bg-white/5 disabled:opacity-40"
                  >
                    <ChevronLeft size={13} /> Zurück
                  </button>
                  <span className="text-[11px] text-fg-muted">{idx + 1} / {aiStore.tools.length}</span>
                  <button
                    onClick={() => setToolIdx((i) => Math.min(aiStore.tools.length - 1, i + 1))}
                    disabled={idx >= aiStore.tools.length - 1}
                    className="flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-xs text-fg transition hover:bg-white/5 disabled:opacity-40"
                  >
                    Weiter <ChevronRight size={13} />
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Resize handles: all 8 edges/corners, Windows-style */}
      {([
        { dir: "n", cls: "top-0 left-2 right-2 h-1.5 cursor-ns-resize" },
        { dir: "s", cls: "bottom-0 left-2 right-2 h-1.5 cursor-ns-resize" },
        { dir: "w", cls: "left-0 top-2 bottom-2 w-1.5 cursor-ew-resize" },
        { dir: "e", cls: "right-0 top-2 bottom-2 w-1.5 cursor-ew-resize" },
        { dir: "ne", cls: "top-0 right-0 h-3 w-3 cursor-nesw-resize" },
        { dir: "nw", cls: "top-0 left-0 h-3 w-3 cursor-nwse-resize" },
        { dir: "se", cls: "bottom-0 right-0 h-3 w-3 cursor-nwse-resize" },
        { dir: "sw", cls: "bottom-0 left-0 h-3 w-3 cursor-nesw-resize" },
      ] as const).map((h) => (
        <div
          key={h.dir}
          onPointerDown={(e) => onResizeDown(e, h.dir)}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeUp}
          className={`absolute z-30 ${h.cls}`}
          title="Größe ändern"
        />
      ))}
    </div>,
    document.body,
  );
});












































